import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const UserLogin = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
      const res = await axios.post(`${API_URL}/api/users/login`, { email, password });
      
      if (res.data.preToken) {
        localStorage.setItem('preToken', res.data.preToken);
        localStorage.setItem('availableWorkspaces', JSON.stringify(res.data.workspaces));
        navigate('/select-workspace');
      } else {
        localStorage.setItem('token', res.data.token);
        localStorage.setItem('workspaceName', res.data.workspaceName);
        localStorage.setItem('user', JSON.stringify(res.data.user));
        localStorage.setItem('userRole', res.data.role);
        navigate(`/workspace/${res.data.workspaceUuid}/dashboard`);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Authentication failed.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.page}>
      {/* clean geometric accents (Casa Libre comic-panel style) */}
      <div style={styles.decoRing} />
      <div style={styles.decoSquare} />
      <div style={styles.decoHatch} className="cl-hatch" />

      <div style={styles.card} className="cl-pop">
        <div style={styles.header}>
          <div style={styles.logoWrap}>
            <img src="/vetobase-icon.png" alt="Vetobase" style={styles.logo} />
          </div>
          <h1 style={styles.title}>Vetobase <span style={styles.titleAccent}>AI</span></h1>
          <p style={styles.subtitle}>Automate Your Business · Sign in to continue</p>
        </div>
        <form onSubmit={handleLogin}>
          <div style={styles.field}>
            <label style={styles.label}>Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={styles.input}
              onFocus={(e) => (e.target.style.borderColor = '#111111')}
              onBlur={(e) => (e.target.style.borderColor = '#E7E1D6')}
              placeholder="e.g. user@company.com"
              required
            />
          </div>
          <div style={styles.field}>
            <label style={styles.label}>Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={styles.input}
              onFocus={(e) => (e.target.style.borderColor = '#111111')}
              onBlur={(e) => (e.target.style.borderColor = '#E7E1D6')}
              placeholder="••••••••"
              required
            />
          </div>
          <button type="submit" disabled={loading} style={styles.button}>
            {loading ? 'Logging in...' : 'Sign In'}
          </button>
          
          {error && <div style={styles.errorBox}>{error}</div>}
        </form>
        <div style={styles.footer}>Secured workspace access · Vetobase</div>
      </div>
    </div>
  );
};

const styles = {
  page: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#F9F4EE',
    backgroundImage: 'radial-gradient(rgba(17,17,17,.05) 1px, transparent 1px)',
    backgroundSize: '22px 22px',
    padding: 20,
    position: 'relative',
    overflow: 'hidden',
    fontFamily: "'Space Grotesk', system-ui, sans-serif"
  },
  // geometric accents — clean, comic-panel style, kept subtle behind the card
  decoRing: { position: 'absolute', top: '-90px', left: '-90px', width: 240, height: 240, borderRadius: '50%', border: '2px solid rgba(17,17,17,.10)', pointerEvents: 'none' },
  decoSquare: { position: 'absolute', bottom: 60, right: '18%', width: 46, height: 46, borderRadius: 12, background: '#FFFFFF', border: '1px solid #E7E1D6', boxShadow: '4px 4px 0 rgba(17,17,17,.1)', transform: 'rotate(12deg)', pointerEvents: 'none' },
  decoHatch: { position: 'absolute', top: '20%', right: '-60px', width: 180, height: 180, borderRadius: 20, opacity: .5, pointerEvents: 'none' },
  card: {
    width: '100%',
    maxWidth: 400,
    background: '#FFFFFF',
    borderRadius: 20,
    padding: '40px 32px',
    border: '1px solid #E7E1D6',
    boxShadow: '5px 5px 0 rgba(17,17,17,.18)',
    position: 'relative',
    zIndex: 1
  },
  header: { textAlign: 'center', marginBottom: 32 },
  logoWrap: { width: 84, height: 84, borderRadius: 22, margin: '0 auto 18px', display: 'grid', placeItems: 'center', background: '#FFFFFF', boxShadow: '5px 5px 0 rgba(17,17,17,.14)', border: '1px solid #E7E1D6' },
  logo: { width: 60, height: 60, borderRadius: 14, objectFit: 'contain', display: 'block' },
  title: { margin: 0, fontSize: 25, fontWeight: 700, letterSpacing: '-0.03em', color: '#111111' },
  titleAccent: { fontFamily: "'Instrument Serif', serif", fontStyle: 'italic', fontWeight: 400, marginLeft: 3 },
  subtitle: { margin: '8px 0 0', color: '#6B6862', fontSize: 14 },
  footer: { marginTop: 22, textAlign: 'center', fontSize: 10, fontWeight: 600, letterSpacing: '.14em', textTransform: 'uppercase', color: '#9C978C', fontFamily: "'IBM Plex Mono', monospace" },
  field: { marginBottom: 20, textAlign: 'left' },
  label: { display: 'block', fontSize: 13, fontWeight: 500, color: '#111111', marginBottom: 6 },
  input: {
    width: '100%',
    padding: '10px 12px',
    borderRadius: 14,
    border: '1px solid #E7E1D6',
    outline: 'none',
    boxSizing: 'border-box',
    fontSize: '14px',
    transition: 'border-color 0.2s'
  },
  button: {
    width: '100%',
    padding: '12px',
    borderRadius: 999,
    border: 'none',
    background: '#111111',
    color: '#F9F4EE',
    fontWeight: 600,
    cursor: 'pointer',
    fontSize: '15px',
    boxShadow: '4px 4px 0 rgba(17,17,17,.2)',
    transition: 'background 0.2s'
  },
  errorBox: {
    fontSize: 13,
    color: '#B0361F',
    textAlign: 'center',
    marginTop: 12,
    background: '#F6E4E1',
    padding: '8px 12px',
    borderRadius: 14,
    border: '1px solid #E7E1D6'
  }
};

export default UserLogin;
